import React from 'react';
import { Link } from "react-router-dom";
import * as Backend from "../backend.js";
import * as util from "../other/util.js";
import { GameData } from "../share/game-data.js";
import BuildingCostFormulas from "../share/formulas/building-cost-formulas.js";
import ResourceVector from "../share/resource-vector.js";
import * as ICONS from "../other/icons.js";
import CompactResourcesView from "./compact-resources-view.js";
import ResourceProductionFormulas from "../share/formulas/resource-production-formulas.js";

// Shows available buildings on a given planet and allows to build them
export default class PlanetConstructionView extends React.Component {

    render() {
        let planet = this.props.planet;

        if (!planet) {
            return (<util.LoadingBar thing={planet} thingName="planet" />);
        }

        return (
            <div>
                <h2>Construction</h2>
                <ConstructionQueue planet={planet} onAbort={(building_id, level) => this.handleAbort(building_id, level)} />
                <BuildingTable category="infrastructure" planet={planet} onBuild={(building) => this.handleConstructionRequest(building)} />

                <BuildingTable category="production" planet={planet} onBuild={(building) => this.handleConstructionRequest(building)} />
            </div>
        );
    }

    handleConstructionRequest(building) {
        let planet = this.props.planet;
        let x = planet.x;
        let y = planet.y;
        let z = planet.z;
        Backend.tryStartConstruction(x, y, z, building.id, (res) => {
            if (res.success) {
                this.props.onUpdatePlanet(res.planet);
            } else {
                alert(res.error);
            }
        });
    }

    handleAbort(building_id, level) {
        let planet = this.props.planet;
        let x = planet.x;
        let y = planet.y;
        let z = planet.z;
        Backend.tryCancelConstruction(x, y, z, building_id, level, (res) => {
            if (res.success) {
                this.props.onUpdatePlanet(res.planet);
            } else {
                alert(res.error);
            }
        });
    }
}

class BuildingTable extends React.Component {
    render() {
        let planet = this.props.planet;
        let category = this.props.category;
        let buildings = GameData.BUILDINGS.filter(building => category === building.ui_category && BuildingCostFormulas.isVisibleToPlayer(building, util.getGPS()));

        if (buildings.length === 0) {
            return null;
        }

        // Capitalize category
        const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);

        return (
            <div>
                <h3>{capitalizedCategory}</h3>
                <table className="resources">
                    <thead>
                        <tr>
                            <td />
                            <td>
                                <div>Building</div>
                            </td>
                            <td>
                                <div>Costs</div>
                            </td>
                            <td>
                                <div>Time</div>
                            </td>
                            <td />
                        </tr>
                    </thead>
                    <tbody>
                        {buildings.map((building, idx) => {
                            return (<SingleBuilding key={idx}
                                planet={planet}
                                building={building}
                                onBuild={() => this.props.onBuild(building, 0)} />);
                        })}
                    </tbody>
                </table>
            </div>);
    }
}

class SingleBuilding extends React.Component {
    render() {
        const planet = this.props.planet;
        const building = this.props.building;

        const updated_building_levels = getUpdatedBuildingLevels(planet);
        const current_level = planet.buildings[building.id];
        const updated_level = updated_building_levels[building.id];
        const next_level = updated_level + 1;
        const central_level = updated_building_levels.central;
        const has_requirements = BuildingCostFormulas.hasRequirements(building, updated_building_levels);
        const cost = BuildingCostFormulas.getCostForUpgrade(building, next_level);
        const time = BuildingCostFormulas.getTime(building, next_level, central_level);
        const has_resources = new ResourceVector().initSparse(planet.resources).hasVectorSparse(cost);
        const is_next_level_available = BuildingCostFormulas.isLevelAvailable(building, util.getGPS().building_limits, next_level);

        const energy_stats = ResourceProductionFormulas.getRawEnergyStats(planet);
        energy_stats.amount = BuildingCostFormulas.getEnergyCostForUpgrade(building, next_level);

        return (
            <tr>
                <td style={{ width: "60px", padding: "0px" }}>
                    <img src={ICONS.BUILDINGS[building.id]} alt={building.ui_name} style={{ width: "100%", marginBottom: "-4px" }} />
                </td>
                <td style={{ paddingLeft: "12px" }}>
                    <Link className="link-button" to={{ pathname: "/game/building-info/" + building.id }}>{building.ui_name}</Link>
                    <div><span className="boring-info-text">level</span> {current_level}{current_level !== updated_level ? <span style={{ color: "yellow" }}> + {(updated_level - current_level)}</span> : null}</div>
                </td>
                <td style={{ textAlign: "right" }}><CompactResourcesView resources={cost} availableResources={planet.resources} energy_stats={energy_stats} /></td>
                <td style={{ textAlign: "right" }}>
                    <util.TimeSpan timeSpan={time} />
                </td>
                <td style={{ textAlign: "center" }}>
                    <StartConstructButton planet={planet} isMaxLevel={!is_next_level_available} building={building}
                        currentLevel={updated_level} hasResources={has_resources}
                        hasRequirements={has_requirements} onBuild={() => this.props.onBuild()}
                        updatedBuildingLevels={updated_building_levels} />
                </td>
            </tr>
        );
    }
}

class StartConstructButton extends React.Component {
    render() {
        let building = this.props.building;
        let current_level = this.props.currentLevel;
        let is_max_level = this.props.isMaxLevel;
        let updated_building_levels = this.props.updatedBuildingLevels;

        if (is_max_level) {
            return (<div className="action-button-inactive">Max Level Reached</div>)
        } else if (!this.props.hasRequirements) {
            return (
                <div>
                    {building.requirements.map((requirement, idx) => {
                        if (updated_building_levels[requirement.id] < requirement.level) {
                            return (<div key={idx}
                                style={{ color: "red" }}>{GameData.buildingName(requirement.id)} ({requirement.level})</div>);
                        } else {
                            return (<div key={idx}
                                style={{ color: "green" }}>{GameData.buildingName(requirement.id)} ({requirement.level})</div>);
                        }
                    })}
                </div>);
        } else if (!this.props.hasResources) {
            return (<div style={{ color: "red" }}>No resources!</div>);
        } else {
            return (
                <div className="action-button" onClick={() => this.props.onBuild()}>build ({current_level + 1})</div>)
        }
    }
}

class ConstructionQueue extends React.Component {
    render() {
        let planet = this.props.planet;
        let building_project = planet.building_project;

        if (building_project.queue.length === 0) {
            return <pre>No active construction project.</pre>;
        }

        return (
            <table className="generic-list" style={{ padding: "16px" }}>
                <thead>
                    <tr>
                        <td>Building</td>
                        <td>Duration</td>
                        <td>Finished</td>
                        <td />
                    </tr>
                </thead>
                <tbody>
                    {
                        building_project.queue.map((task, idx) => {
                            let building = GameData.BUILDING_BY_ID[task.building_id];
                            return (<tr key={idx}>
                                <td style={{ width: "40%" }}>
                                    {building.ui_name} Level: {task.level}
                                </td>
                                <td style={{ width: "20%", paddingRight: "12px" }}>
                                    {idx === 0
                                        ? <util.Timer style={{ display: "block" }} date={task.finished}
                                            ready={<Link className="link-button"
                                                to={"/game/planet-construction" + util.getPlanetUrl(planet)}>refresh: &#x21bb;</Link>} />
                                        :
                                        <util.TimeSpan style={{ textAlign: "right", display: "block" }} timeSpan={task.time} />
                                    }
                                </td>
                                <td>
                                    {util.RealTime.timePointToHtml(task.finished)}
                                </td>
                                <td>
                                    <div className="link-button" onClick={() => this.props.onAbort(task.building_id, task.level)}>abort</div>
                                </td>
                            </tr>);
                        })
                    }
                </tbody>
            </table>
        );
    }
}

const getUpdatedBuildingLevels = (planet) => {
    const levels = JSON.parse(JSON.stringify(planet.buildings));
    planet.building_project.queue.forEach((task) => {
        levels[task.building_id] = task.level;
    });
    return levels;
}
